import {
  FrequencyUnit,
  Habit,
  HabitSchedule,
  InsightsTimeframes,
  ScheduleEntryState,
  Timeframe,
  Timestamp,
  WeekDays,
} from '@griply/types';
import {
  getActiveSchedule,
  getActualScheduleStartDate,
  getDisplayCompletionPercentage,
  getScheduleEndDate,
  getScheduleFrequency,
  getScheduleTimeframeTarget,
  getTimeframeSchedules,
  getTimeframesForDateRange,
  Interval,
  isBetween,
} from '@griply/utils';
import {
  addMilliseconds,
  differenceInYears,
  isAfter,
  isSameDay,
  startOfDay,
} from 'date-fns';
import { useMemo } from 'react';

import { useInsightTimeframePerfectSlots } from './use-insight-timeframe-perfect-slots';
import { useTimeframeOccurrences } from './use-timeframe-occurrences';

export type BarChartEntry = {
  date: Timestamp;
  value: number;
  target: number;
  isFaded: boolean;
  isPerfect: boolean;
  label: string;
  tooltip: string;
};

export type LabelFormatterContext = {
  startDate: Timestamp;
  endDate: Timestamp;
  weekStartsOn: WeekDays;
};

export type TooltipFormatterContext = {
  value: number;
  target: number;
  timeframe: Timeframe;
};

export type Options = {
  today: Timestamp;
  timeframe: InsightsTimeframes;
  startDate: Timestamp;
  endDate: Timestamp;
  weekStartsOn?: WeekDays;
  labelFormatter?: (date: Timestamp, ctx: LabelFormatterContext) => string;
  tooltipFormatter?: (ctx: TooltipFormatterContext) => string;
};

export const useHabitBarChartEntries = (
  habit: Habit,
  {
    today,
    timeframe,
    startDate,
    endDate,
    weekStartsOn = WeekDays.Monday,
    labelFormatter,
    tooltipFormatter,
  }: Options,
) => {
  const { occurrences } = useTimeframeOccurrences(habit.schedules, {
    startDate,
    endDate,
  });
  const perfectSlots = useInsightTimeframePerfectSlots(habit.schedules, {
    timeframe,
    startDate,
    endDate,
    weekStartsOn,
    today,
  });

  // get the active schedule to base the entry-range upon
  const activeSchedule = useMemo(
    () => getActiveSchedule(habit.schedules, today),
    [habit.schedules, today],
  );

  const entries = useMemo((): BarChartEntry[] => {
    if (!activeSchedule) {
      return [];
    }
    const frequency = getScheduleFrequency(activeSchedule);

    // When the last schedule in the displayed timeframe is a frequency schedule,
    // rrule-occurrences should not contribute to the target
    const displayedTimeframeSchedules = getTimeframeSchedules(habit.schedules, {
      startDate,
      endDate,
    });
    const lastTimeframeSchedule = displayedTimeframeSchedules.reduce<
      HabitSchedule | undefined
    >(
      (acc, current) =>
        acc && isAfter(current.startDate, acc.startDate)
          ? current
          : acc ?? current,
      undefined,
    );
    const lastScheduleIsFrequency = !!lastTimeframeSchedule?.frequency;

    const timeframeIntervalMap = {
      [InsightsTimeframes.Week]: Interval.Day,
      [InsightsTimeframes.Month]:
        activeSchedule.frequency?.unit === FrequencyUnit.Week
          ? Interval.Week
          : Interval.Month,
      [InsightsTimeframes.Quarter]:
        frequency === FrequencyUnit.Week ? Interval.Week : Interval.Month,
      [InsightsTimeframes.Year]: Interval.Month,
      [InsightsTimeframes.FourWeeks]: Interval.Week,
      [InsightsTimeframes.TwelveWeeks]: Interval.Week,
      [InsightsTimeframes.TwelveMonths]: Interval.Month,
    };
    // get all dates between start and end-date
    const timeframes = getTimeframesForDateRange(startDate, endDate, {
      weekStartsOn,
      interval: timeframeIntervalMap[timeframe],
    });

    // create empty entries for the timeframes
    return timeframes.map<BarChartEntry>((timeframeEntry) => {
      const schedulesForTimeframe = getTimeframeSchedules(habit.schedules, {
        startDate: timeframeEntry.startDate,
        endDate: timeframeEntry.endDate,
      });
      const { timeframeCompletions, timeframeTarget } =
        schedulesForTimeframe.reduce(
          (acc, schedule) => {
            // completions
            const scheduleCompletions = schedule.entries.filter(
              ({ state }) => state === ScheduleEntryState.Complete,
            );
            // get all completions within the timeframe
            const scheduleCompletionsForTimeframe =
              scheduleCompletions.filter(({ date }) =>
                isBetween(
                  date,
                  timeframeEntry.startDate,
                  timeframeEntry.endDate,
                ),
              ) ?? [];

            // target
            // get the start of the range of the schedule
            const scheduleStartDate = getActualScheduleStartDate(schedule);
            const scheduleEndDate = getScheduleEndDate(schedule);

            let target = 0;
            // get the start and end of the timeframe, based on schedule duration
            const _start = new Date(
              Math.max(
                timeframeEntry.startDate.getTime(),
                startOfDay(scheduleStartDate).getTime(),
              ),
            );
            const _end = scheduleEndDate
              ? new Date(
                  Math.min(
                    scheduleEndDate.getTime(),
                    timeframeEntry.endDate.getTime(),
                  ),
                )
              : timeframeEntry.endDate;

            // get the target for the schedule, for this timeframe
            target = schedule.frequency
              ? getScheduleTimeframeTarget(schedule, {
                  startDate: _start,
                  endDate: _end,
                  weekStartsOn,
                })
              : lastScheduleIsFrequency
                ? 0
                : occurrences.filter((date) => isBetween(date, _start, _end))
                    .length * schedule.completionTarget.count;

            acc.timeframeCompletions +=
              scheduleCompletionsForTimeframe.reduce<number>(
                (completionAcc, { value }) => completionAcc + (value ?? 0),
                0,
              );
            acc.timeframeTarget += target;
            return acc;
          },
          { timeframeCompletions: 0, timeframeTarget: 0 },
        );
      return {
        date: timeframeEntry.referenceDate,
        value: timeframeCompletions,
        target: timeframeTarget,
        isFaded: isAfter(timeframeEntry.referenceDate, today),
        isPerfect: !!perfectSlots.find(
          (slot) =>
            (timeframe === InsightsTimeframes.Week &&
              isBetween(
                timeframeEntry.startDate,
                slot.startDate,
                slot.endDate,
              )) ||
            (isSameDay(timeframeEntry.startDate, slot.startDate) &&
              isSameDay(timeframeEntry.endDate, slot.endDate)),
        ),
        label:
          labelFormatter?.(timeframeEntry.referenceDate, {
            startDate,
            endDate,
            weekStartsOn,
          }) ?? '',
        tooltip:
          tooltipFormatter?.({
            value: timeframeCompletions,
            target: timeframeTarget,
            timeframe: timeframeEntry,
          }) ?? '',
      };
    });
  }, [
    activeSchedule,
    endDate,
    habit.schedules,
    labelFormatter,
    occurrences,
    perfectSlots,
    startDate,
    timeframe,
    today,
    weekStartsOn,
  ]);

  const entriesTarget = useMemo(
    () => Math.max(...entries.map(({ target }) => target), 1),
    [entries],
  );

  const entriesValue = useMemo(
    () => entries.reduce((acc, { value }) => acc + value, 0),
    [entries],
  );

  // statistics
  const { totalEntriesTarget, completionCount } = useMemo(
    () =>
      entries.reduce(
        (acc, { value, target }) => {
          acc.completionCount += value;
          acc.totalEntriesTarget += target ?? 0;
          return acc;
        },
        { totalEntriesTarget: 0, completionCount: 0 },
      ),
    [entries],
  );
  const yearTarget = useMemo(() => {
    const timeframeSchedules = getTimeframeSchedules(habit.schedules, {
      startDate: startDate,
      endDate: endDate,
    });
    const lastSchedule = timeframeSchedules.reduce<HabitSchedule | undefined>(
      (acc, current) =>
        acc && isAfter(current.startDate, acc.startDate)
          ? current
          : acc ?? current,
      undefined,
    );
    if (lastSchedule?.frequency?.unit === FrequencyUnit.Year) {
      // adding a millisecond to make sure the start- and end-date are in different years when selecting a perfect year
      const years = differenceInYears(addMilliseconds(endDate, 1), startDate);
      return lastSchedule?.frequency.count * years;
    }

    return 0;
  }, [endDate, habit.schedules, startDate]);

  const timeframeTargetMap = {
    [InsightsTimeframes.Week]:
      activeSchedule?.frequency?.unit === FrequencyUnit.Week
        ? activeSchedule.frequency.count
        : totalEntriesTarget,
    [InsightsTimeframes.Month]:
      activeSchedule?.frequency?.unit === FrequencyUnit.Month
        ? activeSchedule.frequency.count
        : totalEntriesTarget,
    [InsightsTimeframes.Quarter]: totalEntriesTarget,
    [InsightsTimeframes.Year]:
      activeSchedule?.frequency?.unit === FrequencyUnit.Year
        ? yearTarget
        : totalEntriesTarget,
    [InsightsTimeframes.FourWeeks]: totalEntriesTarget,
    [InsightsTimeframes.TwelveWeeks]: totalEntriesTarget,
    [InsightsTimeframes.TwelveMonths]: totalEntriesTarget,
  };

  const target = timeframeTargetMap[timeframe];

  return {
    entries,
    perfectSlots,
    entriesValue,
    entriesTarget,
    target,
    completionPercentage: target
      ? getDisplayCompletionPercentage(completionCount, target)
      : undefined,
  };
};
